import { createContext, useContext, useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { useCommentsCountsByPost } from '../../hooks/comments/useCommentsCountsByPost'
import type { CommentCountsByPost } from '../../hooks/comments/useCommentsCountsByPost'

// ========================================
// Context Types
// ========================================

type CommentsByPostCountsContextValue = {
  countsByPostId: Record<string, CommentCountsByPost>
  loading: boolean
  /** Set when the counts-by-post query failed. Consumers can read and react to fetch failures. */
  error: Error | null
}

// ========================================
// Context Definition
// ========================================

const defaultValue: CommentsByPostCountsContextValue | null = null

export const CommentsByPostCountsContext =
  createContext<CommentsByPostCountsContextValue | null>(defaultValue)

// ========================================
// Provider Component
// ========================================

export type CommentsByPostCountsProviderProps = {
  postIds: string[]
  /**
   * True when the post list is filtered to posts that have comments, so every
   * post in `postIds` must get a counts row. Gates the omitted-row telemetry:
   * the unfiltered list carries posts with no comments, which the counts
   * aggregation omits by design.
   */
  everyPostHasComments: boolean
  children: ReactNode
}

/**
 * Provides comment counts per post to the comments-by-post view tree.
 * Fetches counts for the given post IDs so descendants (e.g. CommentMetrics)
 * can read counts via useCommentCountsForPost(postId) without prop drilling.
 */
export function CommentsByPostCountsProvider({
  postIds,
  everyPostHasComments,
  children,
}: CommentsByPostCountsProviderProps): JSX.Element {
  const { countsByPostId, loading, error } = useCommentsCountsByPost(postIds, {
    everyPostHasComments,
  })

  const value: CommentsByPostCountsContextValue = useMemo(
    () => ({
      countsByPostId,
      loading,
      error: error ?? null,
    }),
    [countsByPostId, loading, error],
  )

  return (
    <CommentsByPostCountsContext.Provider value={value}>
      {children}
    </CommentsByPostCountsContext.Provider>
  )
}

CommentsByPostCountsProvider.displayName = 'CommentsByPostCountsProvider'

// ========================================
// Consumer Hook
// ========================================

export type CommentCountsForPostResult = {
  counts: CommentCountsByPost | undefined
  loading: boolean
  /** Present when the counts-by-post query failed. */
  error: Error | null
}

/**
 * Returns comment counts for a single post when used inside CommentsByPostCountsProvider.
 * Outside the provider (e.g. Storybook, tests), returns { counts: undefined, loading: false }
 * so components can render safely without a provider.
 */
export function useCommentCountsForPost(
  postId: string,
): CommentCountsForPostResult {
  const context = useContext(CommentsByPostCountsContext)

  if (context === null) {
    return { counts: undefined, loading: false, error: null }
  }

  const counts = context.countsByPostId[postId]

  return {
    counts,
    // Only report loading for posts that don't have data yet.
    // Already-fetched posts keep showing their data while new pages load.
    loading: counts === undefined && context.loading,
    error: context.error,
  }
}
